import { ApiError } from '~/api/errors';
import i18n from '~/i18n.config';

import type { TransformerErrorCode, TransformerProblem } from '../types/transformer.types';

const KNOWN_CODES: readonly TransformerErrorCode[] = [
  'NAME_TAKEN',
  'IN_USE',
  'DUPLICATE',
  'FILE_NAME_CLASH',
  'CONNECTOR_MISMATCH',
  'INVALID_DEFINITION',
  'ARCHIVED',
  'VERSION_NOT_FOUND',
  'SUBSIDIARY_MISMATCH',
  'NO_TRANSFORMER',
  'ENGINE_UNAVAILABLE',
];

/** Codes whose raw server text is replaced by a friendly, translated sentence. */
const FRIENDLY_KEYS: Partial<Record<TransformerErrorCode, string>> = {
  ENGINE_UNAVAILABLE: 'transformers:errors.engineUnavailable',
  INVALID_DEFINITION: 'transformers:errors.invalidDefinition',
  ARCHIVED: 'transformers:errors.archived',
  VERSION_NOT_FOUND: 'transformers:errors.versionNotFound',
  SUBSIDIARY_MISMATCH: 'transformers:errors.subsidiaryMismatch',
};

/** How many problems a toast lists before summarising the rest. */
const TOAST_PROBLEM_LIMIT = 3;

/**
 * The §7 error code behind a failed transformer call. Reads the structured
 * `serverCode` first (integrations' `code` / `error_code`), then scans the message
 * list — a passed-through engine error may only name the code in `errors[]`.
 */
export function transformerErrorCode(err: unknown): TransformerErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  const direct = KNOWN_CODES.find((code) => code === err.serverCode);
  if (direct) return direct;
  const text = [err.message, ...err.messages].join(' ');
  return KNOWN_CODES.find((code) => new RegExp(`\\b${code}\\b`).test(text)) ?? null;
}

/** Narrow one raw `problems[]` entry to a `TransformerProblem`, or null if malformed. */
function toProblem(raw: unknown): TransformerProblem | null {
  if (raw === null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.message !== 'string') return null;
  const level = p.level === 'warning' || p.level === 'info' ? p.level : 'error';
  return {
    level,
    code: typeof p.code === 'string' ? p.code : 'INVALID_DEFINITION',
    message: p.message,
    ...(typeof p.stepIndex === 'number' ? { stepIndex: p.stepIndex } : {}),
    ...(typeof p.fileStepIndex === 'number' ? { fileStepIndex: p.fileStepIndex } : {}),
    ...(typeof p.path === 'string' ? { path: p.path } : {}),
  };
}

/**
 * The `problems[]` of a transformer error body (422 `INVALID_DEFINITION` on create /
 * save) — the same `Problem` shape as preview checks, so the editor can show them inline.
 */
export function transformerErrorProblems(err: unknown): TransformerProblem[] {
  if (!(err instanceof ApiError)) return [];
  const body = err.body;
  if (body === null || typeof body !== 'object' || !('problems' in body)) return [];
  const raw = (body as { problems: unknown }).problems;
  if (!Array.isArray(raw)) return [];
  return raw.map(toProblem).filter((p): p is TransformerProblem => p !== null);
}

/**
 * True when a preview failed because the subsidiary has no completed run to sample
 * (integrations answers 404 "No completed GMC run for subsidiary …"). Screens show a
 * friendly empty state for it instead of the raw server sentence.
 */
export function isNoRunError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.statusCode === 404) return true;
  return /no completed \w+ run/i.test([err.message, ...err.messages].join(' '));
}

/** "(step 2) message" — one problem as a single toast-friendly line. */
function problemLine(p: TransformerProblem): string {
  if (p.stepIndex !== undefined) {
    return `${i18n.t('transformers:preview.atStep', { n: p.stepIndex + 1 })} ${p.message}`;
  }
  if (p.fileStepIndex !== undefined) {
    return `${i18n.t('transformers:preview.atFileStep', { n: p.fileStepIndex + 1 })} ${p.message}`;
  }
  return p.message;
}

/**
 * The message to show for a failed transformer call. Known codes with a friendly
 * sentence use it (plus up to three `problems[]` lines for an invalid definition);
 * otherwise the server's own explanation, minus a leading `CODE:` token.
 */
export function transformerErrorMessage(err: unknown, fallback: string): string {
  const code = transformerErrorCode(err);
  const friendlyKey = code ? FRIENDLY_KEYS[code] : undefined;
  if (friendlyKey) {
    const friendly = i18n.t(friendlyKey);
    const problems = transformerErrorProblems(err).filter((p) => p.level === 'error');
    if (problems.length === 0) return friendly;
    const listed = problems.slice(0, TOAST_PROBLEM_LIMIT).map(problemLine).join('; ');
    const more = problems.length - TOAST_PROBLEM_LIMIT;
    return more > 0
      ? `${friendly} ${listed}; ${i18n.t('transformers:errors.moreProblems', { count: more })}`
      : `${friendly} ${listed}`;
  }
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : fallback;
  const text = err.messages.join(' ').trim() || err.message;
  return text.replace(/^[A-Z_]+:\s*/, '') || fallback;
}
